

import React, { useEffect, useState } from "react";
import InstitutionLayout from "../components/InstitutionLayout";
import { useAuth } from "../App";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";
import { Upload, Save } from "lucide-react";

export default function InstitutionProfile() {
  const { axiosInstance, user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [files, setFiles] = useState({});
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const res = await axiosInstance.get(`/institutions/profile`);
      setProfile(res.data);
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors du chargement du profil");
    }
  };

  const handleChange = (field, value) => {
    setProfile((prev) => ({ ...prev, [field]: value }));
  };

  const handleTarifChange = (roleType, field, value) => {
    setProfile((prev) => ({
      ...prev,
      tarification: {
        ...prev.tarification,
        [roleType]: {
          ...prev.tarification?.[roleType],
          [field]: value,
        },
      },
    }));
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...profile,
        tarification: {
          infirmier: Object.fromEntries(
            Object.entries(profile.tarification?.infirmier || {}).map(
              ([k, v]) => [k, parseFloat(v) || 0]
            )
          ),
          aide_soignant: Object.fromEntries(
            Object.entries(profile.tarification?.aide_soignant || {}).map(
              ([k, v]) => [k, parseFloat(v) || 0]
            )
          ),
        },
      };

      await axiosInstance.put(`/institutions/${profile.id}`, payload);
      toast.success("Profil mis à jour avec succès !");
      fetchProfile();
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de la mise à jour");
    }
  };

  const handleFileUpload = async (name, file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      setUploading(true);

      await axiosInstance.post(
        `/institutions/${profile.id}/upload/${name}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );

      toast.success(`Document ${name} envoyé avec succès`);
    } catch (err) {
      console.error(err);
      toast.error(`Échec de l’envoi du document ${name}`);
    } finally {
      setUploading(false);
    }
  };

  if (!profile) {
    return (
      <InstitutionLayout title="Profil de l'établissement">
        <div className="flex min-h-[300px] items-center justify-center">
          <div className="rounded-xl border border-[#C1DDDD] bg-white px-6 py-4 text-[#3F7373] shadow-sm">
            Chargement...
          </div>
        </div>
      </InstitutionLayout>
    );
  }

  const tarifFields = [
    ["weekday_rate", "Jour semaine (€ / h)"],
    ["weekend_rate", "Week-end (€ / h)"],
    ["night_week_rate", "Nuit semaine (€ / h)"],
    ["night_weekend_rate", "Nuit week-end (€ / h)"],
    ["travel_weekday_fee", "Déplacement jour semaine (€)"],
    ["travel_weekend_fee", "Déplacement week-end (€)"],
    ["travel_night_week_fee", "Déplacement nuit semaine (€)"],
    ["travel_night_weekend_fee", "Déplacement nuit week-end (€)"],
  ];

  const documentsList = [
    { key: "contract", label: "Contrat" },
    { key: "identity_card", label: "Carte d’identité" },
    { key: "diplomas", label: "Diplômes" },
    { key: "visa_inami", label: "Visa et INAMI" },
    { key: "certificate", label: "Certificat de bonne vie et mœurs" },
  ];

  const cardClass =
    "border-[#C1DDDD] bg-white shadow-sm transition-shadow hover:shadow-md";

  const headingClass =
    "mb-4 border-b border-[#D4EBEB] pb-3 text-xl font-bold text-[#3F7373]";

  const inputClass =
    "border-[#C1DDDD] bg-white focus-visible:ring-[#6FAFAF] focus-visible:border-[#6FAFAF]";

  const primaryButtonClass =
    "bg-[#6FAFAF] text-white hover:bg-[#4F8585] transition-colors";

  return (
    <InstitutionLayout title="Profil de l'établissement">
      <div className="mb-6 rounded-2xl border border-[#C1DDDD] bg-[#D4EBEB] p-6">
        <h1 className="text-2xl font-bold text-[#3F7373]">
          Informations de l'établissement
        </h1>
        <p className="mt-2 text-sm text-[#4F8585]">
          Consultez et mettez à jour les coordonnées ainsi que les tarifs de
          votre établissement.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* Informations générales */}
        <Card className={cardClass}>
          <CardContent className="space-y-4 p-6">
            <h2 className={headingClass}>Informations générales</h2>

            <div className="space-y-2">
              <Label className="text-[#3F7373]">Nom</Label>
              <Input
                className={inputClass}
                value={profile.name || ""}
                onChange={(e) => handleChange("name", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[#3F7373]">Email</Label>
              <Input
                className={inputClass}
                type="email"
                value={profile.email || ""}
                onChange={(e) => handleChange("email", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[#3F7373]">Téléphone</Label>
              <Input
                className={inputClass}
                value={profile.phone || ""}
                onChange={(e) => handleChange("phone", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label className="text-[#3F7373]">Adresse</Label>
              <Input
                className={inputClass}
                value={profile.address || ""}
                onChange={(e) => handleChange("address", e.target.value)}
              />
            </div>

            <Button
              onClick={handleSave}
              className={`mt-4 w-full ${primaryButtonClass}`}
            >
              <Save className="mr-2" size={18} />
              Enregistrer
            </Button>
          </CardContent>
        </Card>

        {/* Tarification Infirmier */}
        <Card className={cardClass}>
          <CardContent className="space-y-3 p-6">
            <h2 className={headingClass}>Tarification Infirmier</h2>

            {tarifFields.map(([key, label]) => (
              <div key={key} className="space-y-2">
                <Label className="text-[#3F7373]">{label}</Label>

                <Input
                  className={inputClass}
                  type="number"
                  step="0.01"
                  min="0"
                  value={profile.tarification?.infirmier?.[key] ?? ""}
                  onChange={(e) =>
                    handleTarifChange("infirmier", key, e.target.value)
                  }
                />
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Tarification Aide-Soignant */}
        <Card className={`${cardClass} lg:col-span-2`}>
          <CardContent className="space-y-3 p-6">
            <h2 className={headingClass}>Tarification Aide-Soignant</h2>

            <div className="grid gap-4 md:grid-cols-2">
              {tarifFields.map(([key, label]) => (
                <div key={key} className="space-y-2">
                  <Label className="text-[#3F7373]">{label}</Label>

                  <Input
                    className={inputClass}
                    type="number"
                    step="0.01"
                    min="0"
                    value={profile.tarification?.aide_soignant?.[key] ?? ""}
                    onChange={(e) =>
                      handleTarifChange("aide_soignant", key, e.target.value)
                    }
                  />
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Documents */}
        {/*
        <Card className={`${cardClass} lg:col-span-2`}>
          <CardContent className="p-6">
            <h2 className={headingClass}>Documents à téléverser</h2>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {documentsList.map((doc) => (
                <div
                  key={doc.key}
                  className="rounded-xl border border-[#C1DDDD] bg-[#F4FAFA] p-4 transition hover:bg-[#D4EBEB]"
                >
                  <Label className="mb-2 block text-[#3F7373]">
                    {doc.label}
                  </Label>

                  <Input
                    className={inputClass}
                    type="file"
                    accept="application/pdf,image/*"
                    onChange={(e) =>
                      setFiles({
                        ...files,
                        [doc.key]: e.target.files?.[0],
                      })
                    }
                  />

                  <Button
                    onClick={() =>
                      handleFileUpload(doc.key, files[doc.key])
                    }
                    disabled={!files[doc.key] || uploading}
                    className={`mt-2 w-full ${primaryButtonClass}`}
                  >
                    <Upload size={16} className="mr-1" />
                    Envoyer
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
        */}
      </div>

      <div className="mt-6">
        <Button
          onClick={handleSave}
          className={`w-full py-6 text-base font-semibold ${primaryButtonClass}`}
        >
          <Save className="mr-2" size={18} />
          Sauvegarder toutes les informations
        </Button>
      </div>
    </InstitutionLayout>
  );
}